import { parse, serialize } from "cookie";
import { parseJson } from "../safeParse/parseJson";

/**
 * Set a cookie
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param maxAge - Expiration time in seconds (default: 7 days)
 */
export const setCookie = (
  name: string,
  value: string,
  maxAge: number = 7 * 24 * 60 * 60
) => {
  document.cookie = serialize(name, value, {
    path: "/",
    maxAge,
    sameSite: "strict",
    secure: process.env.NODE_ENV === "production",
  });
};

/**
 * Get a cookie value by name
 * @param name - The name of the cookie
 * @returns The cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (typeof window !== 'undefined') {
    const cookies = parse(document.cookie);
    return cookies[name] || null;
  }
  return null
};

/**
 * Remove a cookie by setting its expiration date to the past
 * @param name - The name of the cookie
 */
export const removeCookie = (name: string) => {
  document.cookie = serialize(name, "", {
    path: "/",
    maxAge: -1,
  });
};

/** Next/Turbopack SSR can stub `localStorage` without `getItem`. */
export const canUseLocalStorage = () => {
  try {
    return typeof window !== "undefined" && typeof localStorage?.getItem === "function"
  } catch {
    return false
  }
}

export const getLocalStorageItem = (key: string): string | null => {
  if (!canUseLocalStorage()) return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export const setLocalStorage = (key: string, value: any): void => {
  if (!canUseLocalStorage()) return
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Safari private mode / full quota
  }
};

/**
 * Get data from localStorage
 * @param key - The key of the stored value
 * @returns The parsed value from localStorage or null if not found
 */
export const getLocalStorage = <T>(key: string): T | null => {
  const storedValue = getLocalStorageItem(key)
  return storedValue ? (parseJson(storedValue) as T) : null
};

/**
 * Remove data from localStorage
 * @param key - The key of the stored value
 */
export const removeLocalStorage = (key: string): void => {
  if (!canUseLocalStorage()) return
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};
